import { JSX } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { formatAutoVehicleLabel } from 'lib/autoTransport/formatAutoVehicleLabel';
import { isAutoTransport } from 'lib/autoTransport/isAutoTransport';
import { AutoTrailerType } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import {
  AdditionalReference,
  CompanyCoreInfo,
  formatAddressCityStateZip,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionChangeRecord,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import {
  getFieldOrder,
  getSuggestionFormattedLabel,
  reorderFields,
} from 'utils/suggestions/suggestionFormat';

import { formatFieldValue } from './utils/suggestionFieldGrouping';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Generates JSX elements for valid suggestion changes.
 * Used by `filterInvalidSuggestions` (length > 0). Quick Quote / Load Building
 * use route summaries when those pipelines have displayable content; otherwise
 * falls through to labeled flattened fields with timezone-aware date formatting.
 */
export const getValidChangeElements = (
  changesDisplayed: SuggestionChangeRecord,
  suggestion: GenericSuggestion
): Undef<JSX.Element>[] => {
  if (suggestion.pipeline === SuggestionPipelines.LoadBuilding) {
    const lbElements = getLoadBuildingSummaryChangeElements(
      suggestion as LoadBuildingSuggestions
    );
    if (lbElements) {
      return lbElements;
    }
  } else if (suggestion.pipeline === SuggestionPipelines.QuickQuote) {
    const qqElements = getQuickQuoteSummaryChangeElements(
      suggestion as SuggestedQuoteChange
    );
    if (qqElements) {
      return qqElements;
    }
  }

  const changesDisplayedList = Object.entries(changesDisplayed);
  if (!changesDisplayedList.length) {
    return [];
  }

  const priorityOrder = getFieldOrder(suggestion.pipeline);
  const orderedFields = reorderFields(changesDisplayedList, priorityOrder);

  return orderedFields
    .map(([name, val]) => {
      const changeLabel = getSuggestionFormattedLabel(
        suggestion.pipeline,
        name
      );
      // Skip empty / zero (flattened empty arrays -> 0)
      if (!changeLabel || !val) {
        return;
      }

      const changeValue = formatFieldValue(name, val, changesDisplayed);
      if (!changeValue) {
        return;
      }

      return getFormattedChangeElement(changeLabel, changeValue);
    })
    .filter(Boolean);
};

/**
 * Quick Quote summary when at least one pickup/delivery address is present.
 * Returns summary elements when valid; null to fall through to field walk.
 */
function getQuickQuoteSummaryChangeElements(
  suggestion: SuggestedQuoteChange
): Maybe<Undef<JSX.Element>[]> {
  const sug = suggestion.suggested;
  if (!sug) {
    return null;
  }

  const pickupAddr = formatQuickQuoteAddress(
    sug.pickupCity,
    sug.pickupState,
    sug.pickupZip
  );
  const dropoffAddr = formatQuickQuoteAddress(
    sug.deliveryCity,
    sug.deliveryState,
    sug.deliveryZip
  );

  if (!pickupAddr && !dropoffAddr) {
    return null;
  }

  const elements: JSX.Element[] = [];

  if (pickupAddr) {
    elements.push(getFormattedChangeElement('Pickup', pickupAddr));
  }
  if (dropoffAddr) {
    elements.push(getFormattedChangeElement('Delivery', dropoffAddr));
  }

  const isLTL = sug.mode?.toUpperCase() === 'LTL';
  if (isLTL) {
    let commodities: unknown[] = [];
    if (Array.isArray(sug.commodities)) {
      commodities = sug.commodities;
    } else if (sug.commodities) {
      commodities = Object.values(sug.commodities);
    }
    const firstCommodity = commodities[0] as Undef<{
      freightClass?: string;
      nmfc?: string;
    }>;
    const freightInfoParts = [
      firstCommodity?.freightClass && `Class ${firstCommodity.freightClass}`,
      firstCommodity?.nmfc && `NMFC ${firstCommodity.nmfc}`,
    ].filter(Boolean);
    if (freightInfoParts.length > 0) {
      elements.push(
        getFormattedChangeElement('Freight Info', freightInfoParts.join(' - '))
      );
    }
  } else if (sug.transportType) {
    elements.push(
      getFormattedChangeElement('Transport Type', String(sug.transportType))
    );
  }

  const autoTransport = sug.autoTransport;
  const suggestedVehicles = asArrayOrObjectValues(autoTransport?.vehicles);
  if (isAutoTransport(sug.transportType) && suggestedVehicles.length) {
    const vehicleLabels = suggestedVehicles
      .map((vehicle) => formatAutoVehicleLabel(vehicle))
      .filter((label) => label.length > 0);
    if (vehicleLabels.length > 0) {
      let vehiclesText = vehicleLabels.join(', ');
      // Suggestion payloads mix casing ("ENCLOSED" vs "enclosed").
      const trailerType = autoTransport?.trailerType?.trim().toLowerCase();
      if (trailerType === AutoTrailerType.Enclosed) {
        vehiclesText = `${vehiclesText} (enclosed trailer)`;
      } else if (trailerType === AutoTrailerType.Open) {
        vehiclesText = `${vehiclesText} (open trailer)`;
      }
      elements.push(getFormattedChangeElement('Vehicles', vehiclesText));
    }
  }

  return elements;
}

/**
 * Load Building summary when address, date, PO, booking, or container is
 * present. Returns summary elements when valid; null to fall through to field
 * walk.
 */
function getLoadBuildingSummaryChangeElements(
  suggestion: LoadBuildingSuggestions
): Maybe<Undef<JSX.Element>[]> {
  const sug = suggestion.suggested;
  if (!sug) {
    return null;
  }

  const pickupAddr = formatAddressCityStateZip(sug.pickup) ?? '';
  const dropoffAddr = formatAddressCityStateZip(sug.consignee) ?? '';

  const pickupDate = formatApptDayInStopTz(
    sug.pickup?.apptStartTime ?? sug.pickup?.readyTime,
    sug.pickup?.timezone
  );
  const dropoffDate = formatApptDayInStopTz(
    sug.consignee?.apptStartTime ??
      sug.consignee?.apptEndTime ??
      sug.consignee?.mustDeliver,
    sug.consignee?.timezone
  );

  const poDisplay = sug.poNums?.trim() ?? '';
  const bookingDisplay = additionalRefNumberByQualifier(
    sug.additionalReferences,
    'Booking'
  );
  const containerDisplay = additionalRefNumberByQualifier(
    sug.additionalReferences,
    'Container'
  );

  if (
    !pickupDate &&
    !dropoffDate &&
    !pickupAddr &&
    !dropoffAddr &&
    !poDisplay &&
    !bookingDisplay &&
    !containerDisplay
  ) {
    return null;
  }

  const elements: JSX.Element[] = [];

  if (pickupAddr || pickupDate) {
    elements.push(
      getFormattedChangeElement(
        'Pickup',
        [pickupAddr, pickupDate].filter(Boolean).join(' - ')
      )
    );
  }
  if (dropoffAddr || dropoffDate) {
    elements.push(
      getFormattedChangeElement(
        'Delivery',
        [dropoffAddr, dropoffDate].filter(Boolean).join(' - ')
      )
    );
  }
  if (poDisplay) {
    elements.push(getFormattedChangeElement('PO #', poDisplay));
  }
  if (bookingDisplay) {
    elements.push(getFormattedChangeElement('Booking #', bookingDisplay));
  }
  if (containerDisplay) {
    elements.push(getFormattedChangeElement('Container #', containerDisplay));
  }
  if (sug.customer?.refNumber) {
    elements.push(
      getFormattedChangeElement('Customer Ref #', sug.customer.refNumber)
    );
  }
  if (sug.customer?.name) {
    elements.push(getFormattedChangeElement('Customer', sug.customer.name));
  }

  return elements.length > 0 ? elements : null;
}

function formatQuickQuoteAddress(
  city: string,
  state: string,
  zip: string
): string {
  if (!city && !state && !zip) {
    return '';
  }

  const address = {
    city,
    state,
    zipCode: zip,
  } as CompanyCoreInfo;

  return formatAddressCityStateZip(address);
}

// Pickup or delivery day in the stop's timezone, or UTC when it has none, e.g. "Sep 29".
export function formatApptDayInStopTz(
  date: Maybe<Date> | MaybeUndef<string>,
  stopIanaTz: Undef<string>
): string {
  if (!date) {
    return '';
  }

  const tz = normalizeTimezone(stopIanaTz);
  if (tz) {
    return dayjs(date).tz(tz).format('MMM D');
  }
  // Never use browser-local timezone for logistics dates.
  return dayjs.utc(date).format('MMM D');
}

function additionalRefNumberByQualifier(
  refs: Undef<AdditionalReference[]>,
  qualifier: string
): string {
  if (!refs?.length) {
    return '';
  }
  const q = qualifier.trim().toLowerCase();
  for (const r of refs) {
    const rq = r.qualifier?.trim().toLowerCase();
    const num = r.number?.trim();
    if (rq === q && num) {
      return num;
    }
  }
  return '';
}

const getFormattedChangeElement = (
  changeLabel: string,
  changeValue: string
): JSX.Element => (
  <div
    key={changeLabel}
    className='text-[12px] whitespace-nowrap overflow-hidden text-ellipsis text-neutral-600'
  >
    <span className='font-medium'>{`${changeLabel}: `}</span>
    {`${changeValue}`}
  </div>
);
